import { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n";
import { useSEO } from "../lib/seo";
import { ServiceIcon } from "./YuxelServiceIcons";
import { getServiceCatalog } from "../data/yuxelServiceCatalog";
import "./YuxelServicesMaster.css";

function ServiceCard({ item }) {
  // Service detail URLs are intentionally not invented.
  return <article className="yx-service-item" id={`service-${item.id}`}>
    <div className="yx-service-item-top"><span className="yx-service-icon"><ServiceIcon name={item.icon} /></span><span className="yx-service-number">{item.number}</span></div>
    <h4>{item.title}</h4><p>{item.description}</p>
  </article>;
}

function Category({ group }) {
  return <section className="yx-category" id={`category-${group.id}`} aria-labelledby={`title-${group.id}`}>
    <div className="yx-category-heading"><span className="yx-category-code">{group.id}</span><div><h3 id={`title-${group.id}`}>{group.title}</h3><p>{group.intro}</p></div></div>
    <div className="yx-service-grid">{group.services.map(s=><ServiceCard key={s.id} item={s}/>)}</div>
  </section>;
}

export default function YuxelServicesMaster(){
  const { href, lang }=useLang();
  const az = lang === "az";
  const { areas: yuxelServiceAreas, categories: yuxelServiceCategories, footer: yuxelServiceFooter } = getServiceCatalog(lang);
  const location = useLocation();
  const navigate = useNavigate();
  const queryArea = new URLSearchParams(location.search).get("area");
  const active = location.hash.startsWith("#category-A") || location.hash === "#ai" ? "ai" : location.hash.startsWith("#category-M") || location.hash === "#marketing" ? "marketing" : queryArea === "ai" ? "ai" : "marketing";
  const categoryGroups=useMemo(()=>yuxelServiceCategories.filter(g=>g.id.startsWith(active==="marketing" ? "M" : "A")),[active, yuxelServiceCategories]);
  useSEO(az ? "YUXEL — Xidmətlər | Marketinq və AI Avtomatlaşdırması" : "YUXEL — Services | Marketing & AI Automation", az ? "Marketinq strategiyasından kreativ işlərə, AI və biznes avtomatlaşdırmasına qədər YUXEL xidmətləri." : "Explore YUXEL services, from marketing strategy and creative direction to AI and business automation.");
  // Switching modes keeps focus near the beginning of the catalog rather than jumping unpredictably.
  useEffect(() => {
    const id = location.hash.slice(1);
    const frame = requestAnimationFrame(() => {
      if (id.startsWith("category-")) document.getElementById(id)?.scrollIntoView({block:"start",behavior:"auto"});
    });
    return () => cancelAnimationFrame(frame);
  }, [active, location.hash]);
  function choose(which) { navigate(`${location.pathname}?area=${which}#${which}`, {replace:true}); }
  const area=yuxelServiceAreas.find(a=>a.id===active);
  return <div className="yx-services" id="yx-services-master" lang={lang}>
    <header className="yx-compact-header yx-wrap">
      <div><p className="yx-overline">YUXEL / {az ? "XİDMƏTLƏR" : "SERVICES"}</p><h1>{az ? "İstiqamətlərimiz" : "Our service areas"}</h1></div>
      <div className="yx-mode-tabs" role="group" aria-label={az ? "Xidmət istiqamətini seçin" : "Choose a service area"}>
        {yuxelServiceAreas.map(a=><button key={a.id} type="button" className={active===a.id?"selected":""} aria-pressed={active===a.id} onClick={()=>choose(a.id)}>{a.label}<ArrowUpRight size={17} aria-hidden="true"/></button>)}
      </div>
    </header>
    <div id="yx-category-top" className="yx-category-top"/>
    <section className="yx-wrap yx-area" aria-live="polite">
      <div className="yx-area-intro"><p className="yx-overline">{area.label}</p><h2>{area.headline}</h2><p>{area.intro}</p>{active === "ai" && <p className="yx-solution-note">{az ? "Bunlar biznesinizə uyğun layihələndirilən həll istiqamətləridir. Əhatə, inteqrasiyalar və insan nəzarəti ilkin təhlildən sonra müəyyənləşdirilir." : "These are solution directions designed for your business. Scope, integrations and human oversight are defined after an initial assessment."}</p>}
        <figure className="yx-area-visual"><img src={active === "marketing" ? "/media/concept/paper-flower.webp" : "/media/concept/newton-red.webp"} alt={active === "marketing" ? (az ? "Marketinq və kreativ istiqamət" : "Marketing and creative direction") : (az ? "AI və əlaqəli proseslər" : "AI and connected processes")} loading="lazy" /></figure>
      </div>
      <nav className="yx-category-nav" aria-label={az ? "Kateqoriyalar" : "Categories"}>
        {categoryGroups.map(g=><a key={g.id} href={`#category-${g.id}`}><ServiceIcon name={g.services[0].icon} size={19}/><span>{g.short}</span><ChevronRightIcon/></a>)}
      </nav>
      {categoryGroups.map(g=><Category group={g} key={g.id}/>)}
      <div className="yx-area-cta"><div><h2>{area.cta.heading}</h2><p>{area.cta.text}</p></div><Link to={href("/contact")}>{area.cta.label}<ArrowUpRight size={18}/></Link></div>
    </section>
    <footer className="yx-services-last"><div className="yx-wrap"><p className="yx-overline">YUXEL / {az ? "NÖVBƏTİ ADDIM" : "NEXT STEP"}</p><h2>{yuxelServiceFooter.heading}</h2><p>{yuxelServiceFooter.text}</p><Link to={href("/contact")}>{yuxelServiceFooter.label} <ArrowUpRight size={19}/></Link></div></footer>
  </div>;
}
function ChevronRightIcon(){return <span aria-hidden="true">↗</span>}
